/**
 * Índice del manual de onboarding.
 *
 * Cada capítulo apunta a un archivo MD dentro de `capitulos/`. El
 * contenido del MD se importa con `?raw` en el componente `ChapterPage`.
 * Añadir un capítulo = añadir entrada aquí + crear el MD + añadir el
 * `?raw` en `chapterContent.ts`.
 */

import type {
  Chapter,
  DocumentEntry,
  FAQEntry,
  Process,
  VideoEntry,
} from "./types";
import processesJson from "./procesos.json";
import documentsJson from "./documentos.json";
import videosJson from "./videos.json";
import faqJson from "./faq.json";

export const chapters: Chapter[] = [
  {
    slug: "conceptos",
    label: "Conceptos base",
    title: "1. Conceptos base",
    summary:
      "Variedades vegetales protegidas y qué es Tang Gold / Tango.",
    contentSlug: "1-conceptos",
    documentSlugs: ["doc-01-variedades-pi"],
    icon: "book",
  },
  {
    slug: "quien-es-quien",
    label: "Quién es quién",
    title: "2. Quién es quién (la cadena)",
    summary:
      "UC, UCR, Eurosemillas, Cyberagropolis y el contrato de licencia.",
    contentSlug: "2-quien-es-quien",
    documentSlugs: ["doc-02-contrato-licencia"],
    icon: "team",
  },
  {
    slug: "control",
    label: "Control",
    title: "3. Control",
    summary: "Nuestra operativa a tres niveles: campo, packing y supermercados.",
    contentSlug: "3-control",
    icon: "audit",
    // Sub-capítulos — cada nivel del control es una página propia
    // para que la lectura no se haga eterna. La portada (`3-control`)
    // es solo el índice + coste común.
    subChapters: [
      {
        slug: "control-campo",
        label: "3.1 Campo",
        title: "3.1 Control · Campo",
        summary:
          "Auditorías de fincas, tipos de auditoría, análisis de muestras de hoja.",
        contentSlug: "3-1-control-campo",
        icon: "audit",
        processSlugs: [
          "auditoria-completa",
          "auditoria-aforo",
          "auditoria-regularizacion",
          "auditoria-localizacion",
          "auditoria-toma-muestras",
        ],
        documentSlugs: [
          "doc-03-planificacion",
          "doc-04-protocolo-completas",
          "doc-05-protocolo-aforo",
          "doc-06-presupuesto",
        ],
        videoSlugs: ["video-hoja"],
      },
      {
        slug: "control-packing",
        label: "3.2 Packing",
        title: "3.2 Control · Packing",
        summary:
          "Auditorías a comercios adheridos (HN) y exportadores autorizados (HS).",
        contentSlug: "3-2-control-packing",
        icon: "audit",
        processSlugs: [
          "auditoria-packing-hn",
          "auditoria-packing-hs",
          "auditoria-trazabilidad",
        ],
        documentSlugs: [
          "doc-07-acuerdo-adhesion",
          "doc-08-protocolo-packing",
          "doc-09-licencia-exportacion",
        ],
        videoSlugs: ["video-cascara"],
      },
      {
        slug: "control-supermercados",
        label: "3.3 Supermercados",
        title: "3.3 Control · Supermercados",
        summary:
          "Verificación en destino, países, ventanas de campaña HN y HS.",
        contentSlug: "3-3-control-supermercados",
        icon: "audit",
        processSlugs: ["auditoria-supermercado"],
        documentSlugs: [
          "doc-10-protocolo-supermercado",
          "doc-11-incidencias-destino",
        ],
        videoSlugs: ["video-cascara"],
      },
    ],
  },
  {
    slug: "certificacion-hn",
    label: "Certificación HN",
    title: "4. Certificación Hemisferio Norte",
    summary:
      "En qué consiste, licenciatarios, comercialización, comercios adheridos.",
    contentSlug: "4-certificacion-hn",
    documentSlugs: ["doc-07-acuerdo-adhesion"],
    icon: "safety",
  },
  {
    slug: "certificacion-hs",
    label: "Certificación HS",
    title: "5. Certificación (exportación) Hemisferio Sur",
    summary:
      "En qué consiste, en qué se basa, exportadores autorizados.",
    contentSlug: "5-certificacion-hs",
    documentSlugs: ["doc-09-licencia-exportacion"],
    icon: "global",
  },
  {
    slug: "promocion",
    label: "Promoción",
    title: "6. Promoción",
    summary:
      "Atributos de la variedad, marca Tango Fruit y programa BeeFriendly (CAAE).",
    contentSlug: "6-promocion",
    documentSlugs: ["doc-14-pliego-beefriendly"],
    icon: "notification",
  },
  {
    slug: "proveedores",
    label: "Proveedores y gestiones",
    title: "7. Proveedores y gestiones del día a día",
    summary: "NACEX, Vistalegre, Naranjasyfrutas y otros.",
    contentSlug: "7-proveedores",
    documentSlugs: ["doc-12-seguimiento-productores", "doc-13-productor-el-mismo"],
    icon: "shopping",
  },
  {
    slug: "conflicto-nadorcott",
    label: "Conflicto Tango / Nadorcott",
    title: "8. Conflicto Tango / Nadorcott",
    summary:
      "Contexto legal: registro de Tang Gold, EDV, resolución de la CPVO y diferencias con Nadorcott.",
    contentSlug: "8-conflicto-nadorcott",
    documentSlugs: ["doc-15-registro-tang-gold-ue"],
    icon: "safety",
  },
];

export const processes: Process[] = processesJson as Process[];
export const documents: DocumentEntry[] = documentsJson as DocumentEntry[];
export const videos: VideoEntry[] = videosJson as VideoEntry[];
export const faqs: FAQEntry[] = faqJson as FAQEntry[];

// ─── Lookups ──────────────────────────────────────────────────────

/** Recorre el árbol de capítulos (padres + subChapters) y devuelve
 *  la lista plana. Útil para lookups por slug y para búsquedas. */
export function flattenChapters(list: Chapter[]): Chapter[] {
  const out: Chapter[] = [];
  for (const c of list) {
    out.push(c);
    if (c.subChapters?.length) {
      out.push(...flattenChapters(c.subChapters));
    }
  }
  return out;
}

export const allChapters = flattenChapters(chapters);

export const chaptersBySlug = new Map(allChapters.map((c) => [c.slug, c]));
export const processesBySlug = new Map(processes.map((p) => [p.slug, p]));
export const documentsBySlug = new Map(documents.map((d) => [d.slug, d]));
export const videosBySlug = new Map(videos.map((v) => [v.slug, v]));
